import React from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Microscope, Handshake, Leaf, Pill, Smartphone, RefreshCw, Monitor, Clock, Phone, Mail } from 'lucide-react'

const C = {
  g900:'#0D2218', g800:'#1A3A28', g700:'#235233', g600:'#2D6B42',
  g500:'#3D8A57', g400:'#5AAE73', g300:'#85C99A', g100:'#E8F5EE', g50:'#F2FAF5',
  white:'#FFFFFF', text:'#1A2820', muted:'#5A7065', border:'#C8DDD2',
}

const STATS = [
  { num:'MD',   label:'Certified Doctors' },
  { num:'200+', label:'Brands Stocked' },
  { num:'Free', label:'Skin Check' },
  { num:'₹500', label:'Online Consult' },
]

const VALUES = [
  { Icon:Microscope, title:'Evidence-Based', desc:'Every treatment we recommend is backed by clinical research. No fads, no guesswork — only what the science supports.' },
  { Icon:Handshake, title:'Personalised Care', desc:'No two patients are the same. We tailor every consultation, regimen, and follow-up plan specifically to you.' },
  { Icon:Leaf, title:'Holistic Approach', desc:'We look beyond the surface — understanding lifestyle, diet, stress, and genetics to treat the root cause.' },
  { Icon:Pill, title:'Prescription Access', desc:'Get medical-grade prescriptions and products delivered directly to your door, fast and affordably.' },
  { Icon:Smartphone, title:'Online Consultations', desc:'Expert dermatologist consultations via Google Meet — from the comfort of your home, for just ₹500.' },
  { Icon:RefreshCw, title:'Structured Follow-ups', desc:'We track your progress, adapt treatments over time, and stay with you every step of the journey.' },
]

const DOCTORS = [
  {
    initial:'L', name:'Dr. Lakshmi Priya',
    qual:'MD, Dermatology.',
    extra:'Dedicated to evidence-based skin & hair care, with personalised treatment plans for every patient.',
    tag:'Dermatologist',
    bg:`linear-gradient(135deg, ${C.g800}, ${C.g600})`,
  },
]

const TIMELINE = [
  { year:'2015', title:'Online Consultations', desc:'Bringing expert teleconsultation for skin & hair to patients across India.' },
  { year:'2019', title:'200+ Brands', desc:'Curated one of India\'s largest collections of medically-vetted dermatological products.' },
  { year:'2023', title:'Nationwide Delivery', desc:'Free delivery of dermatologist-prescribed skincare to patients across India.' },
  { year:'2024', title:'Revive Launched', desc:'A digital-first platform for skin & hair care across India.' },
]

export default function About() {
  const navigate = useNavigate()

  return (
    <div style={{ background:'white', minHeight:'100vh' }}>

      {/* ── HERO ── */}
      <div style={{ background:`linear-gradient(145deg,${C.g900},${C.g800},${C.g700})`, padding:'2.6rem 2rem 2.4rem', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`radial-gradient(circle, rgba(255,255,255,.025) 1px, transparent 1px)`, backgroundSize:'28px 28px', pointerEvents:'none' }} />
        <div style={{ position:'absolute', right:'-5%', top:'-20%', width:500, height:500, borderRadius:'50%', background:`radial-gradient(circle,rgba(93,174,115,.12) 0%,transparent 65%)`, pointerEvents:'none' }} />
        <div style={{ position:'absolute', right:'8%', top:'50%', transform:'translateY(-50%)', width:360, height:360, borderRadius:'50%', border:'1px solid rgba(255,255,255,.06)', pointerEvents:'none' }} />

        <div style={{ maxWidth:1600, margin:'0 auto', position:'relative', zIndex:1 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.15)', borderRadius:999, padding:'.35rem 1rem', marginBottom:'.9rem' }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:C.g300, display:'inline-block' }} />
            <span style={{ fontSize:'.72rem', color:'rgba(255,255,255,.7)', letterSpacing:'.14em', textTransform:'uppercase' }}>Our Story</span>
          </div>

          <div className="resp-2col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4rem', alignItems:'center' }}>
            <div>
              <h1 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.7rem,3.5vw,2.4rem)', color:'white', lineHeight:1.1, marginBottom:'.8rem' }}>
                Trusted skin &<br/>hair care, <em style={{ color:C.g300, fontStyle:'italic' }}>online</em>
              </h1>
              <p style={{ fontSize:'.92rem', color:'rgba(255,255,255,.65)', lineHeight:1.6, marginBottom:'1.2rem', fontWeight:300, maxWidth:480 }}>
                Revive is an online dermatology practice focused on clinically-backed treatments and personalised skincare. We combine medical expertise with curated products to help your skin look and feel its best — all from the comfort of your home.
              </p>
              <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap' }}>
                <button onClick={() => navigate('/book-appointment')} style={{ background:C.g400, color:C.g900, padding:'.85rem 2rem', borderRadius:10, fontSize:'.875rem', fontWeight:600, border:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", letterSpacing:'.04em', boxShadow:'0 4px 20px rgba(90,174,115,.35)' }}>
                  Book Consultation — ₹500
                </button>
                <Link to="/contact" style={{ background:'rgba(255,255,255,.1)', color:'white', padding:'.85rem 2rem', borderRadius:10, fontSize:'.875rem', fontWeight:500, border:'1px solid rgba(255,255,255,.2)', textDecoration:'none', display:'inline-flex', alignItems:'center' }}>
                  Contact Us
                </Link>
              </div>
            </div>

            {/* Stats card */}
            <div style={{ background:'rgba(255,255,255,.07)', backdropFilter:'blur(12px)', border:'1px solid rgba(255,255,255,.12)', borderRadius:24, padding:'2rem' }}>
              <div style={{ fontSize:'.68rem', letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(255,255,255,.4)', marginBottom:'1.5rem' }}>By The Numbers</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
                {STATS.map(s => (
                  <div key={s.label} style={{ background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', borderRadius:14, padding:'1.5rem', textAlign:'center' }}>
                    <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'2rem', color:C.g300, lineHeight:1 }}>{s.num}</div>
                    <div style={{ fontSize:'.68rem', color:'rgba(255,255,255,.45)', letterSpacing:'.08em', textTransform:'uppercase', marginTop:'.4rem' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CLINIC INFO ── */}
      <div style={{ background:C.g50, padding:'4.5rem 2rem' }}>
        <div style={{ maxWidth:1600, margin:'0 auto' }}>
          <div className="resp-2col" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3rem', alignItems:'start' }}>

            {/* Clinic Details */}
            <div>
              <div style={{ fontSize:'.68rem', letterSpacing:'.18em', textTransform:'uppercase', color:C.g600, marginBottom:'.65rem', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ display:'inline-block', width:28, height:1.5, background:C.g500 }} />Get In Touch
              </div>
              <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.8rem,3vw,2.5rem)', color:C.g900, marginBottom:'2rem', lineHeight:1.15 }}>Consult Online</h2>

              {[
                { Icon:Monitor, label:'Consultations', value:'Online via Google Meet' },
                { Icon:Clock, label:'Available', value:'7 days a week' },
                { Icon:Phone, label:'Phone', value:'+91 98765 43210' },
                { Icon:Mail, label:'Email', value:'care@reviveskin.in' },
              ].map(item => {
                const Ic = item.Icon
                return (
                <div key={item.label} style={{ display:'flex', gap:'1rem', padding:'.9rem 0', borderBottom:`1px solid ${C.border}`, alignItems:'flex-start' }}>
                  <span style={{ flexShrink:0, marginTop:2, color:C.g600 }}><Ic size={18} strokeWidth={1.75} /></span>
                  <div>
                    <div style={{ fontSize:'.72rem', letterSpacing:'.08em', textTransform:'uppercase', color:C.muted, marginBottom:'.2rem' }}>{item.label}</div>
                    <div style={{ fontSize:'.9rem', fontWeight:500, color:C.text }}>{item.value}</div>
                  </div>
                </div>
                )
              })}

              <button onClick={() => navigate('/book-appointment')} style={{ marginTop:'1.75rem', background:C.g700, color:'white', padding:'.8rem 2rem', borderRadius:10, fontSize:'.85rem', fontWeight:600, border:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", letterSpacing:'.04em', boxShadow:`0 2px 8px rgba(35,82,51,.25)` }}>
                Book Appointment
              </button>
            </div>

            {/* Our Approach */}
            <div>
              <div style={{ fontSize:'.68rem', letterSpacing:'.18em', textTransform:'uppercase', color:C.g600, marginBottom:'.65rem', display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ display:'inline-block', width:28, height:1.5, background:C.g500 }} />Our Approach
              </div>
              <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.8rem,3vw,2.5rem)', color:C.g900, marginBottom:'1.25rem', lineHeight:1.15 }}>Why Choose Revive?</h2>
              <p style={{ fontSize:'.9rem', color:C.muted, lineHeight:1.8, marginBottom:'2rem' }}>
                We combine the precision of medical dermatology with the accessibility of modern digital care. Whether you're visiting our clinic or consulting online, you get the same level of expertise and attention.
              </p>
              <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:16, padding:'1.5rem', marginBottom:'1rem' }}>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:C.g800, marginBottom:'.5rem' }}>Online Consultation</div>
                <p style={{ fontSize:'.83rem', color:C.muted, lineHeight:1.7, marginBottom:'1rem' }}>Can't visit the clinic? Consult our dermatologist via Google Meet in the comfort of your home — same expertise, fraction of the cost.</p>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'.5rem' }}>
                  <span style={{ background:C.g100, color:C.g700, padding:'.3rem .9rem', borderRadius:999, fontSize:'.78rem', fontWeight:600 }}>Just ₹500</span>
                  <span style={{ fontSize:'.75rem', color:C.muted }}>15–20 min session</span>
                </div>
              </div>
              <div style={{ background:C.g700, borderRadius:16, padding:'1.5rem' }}>
                <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.1rem', color:'white', marginBottom:'.5rem' }}>Free Product Delivery</div>
                <p style={{ fontSize:'.83rem', color:'rgba(255,255,255,.65)', lineHeight:1.7 }}>All dermatologist-prescribed products are delivered to your doorstep — free of charge, pan India.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── OUR VALUES ── */}
      <div style={{ padding:'5rem 2rem' }}>
        <div style={{ maxWidth:1600, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'3.5rem' }}>
            <div style={{ fontSize:'.68rem', letterSpacing:'.18em', textTransform:'uppercase', color:C.g600, marginBottom:'.65rem', justifyContent:'center', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ display:'inline-block', width:28, height:1.5, background:C.g500 }} />What We Stand For
            </div>
            <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.8rem,3.5vw,2.75rem)', color:C.g900 }}>Our Values</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:'1.25rem' }}>
            {VALUES.map(v => <ValueCard key={v.title} {...v} />)}
          </div>
        </div>
      </div>

      {/* ── DOCTORS ── */}
      <div style={{ background:C.g50, padding:'5rem 2rem' }}>
        <div style={{ maxWidth:1600, margin:'0 auto' }}>
          <div style={{ marginBottom:'3.5rem' }}>
            <div style={{ fontSize:'.68rem', letterSpacing:'.18em', textTransform:'uppercase', color:C.g600, marginBottom:'.65rem', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ display:'inline-block', width:28, height:1.5, background:C.g500 }} />Expert Team
            </div>
            <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.8rem,3.5vw,2.75rem)', color:C.g900 }}>Meet the Doctors</h2>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:'2rem' }}>
            {DOCTORS.map(d => <DoctorCard key={d.name} {...d} />)}
          </div>
        </div>
      </div>

      {/* ── TIMELINE ── */}
      <div style={{ padding:'5rem 2rem' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:'3.5rem' }}>
            <div style={{ fontSize:'.68rem', letterSpacing:'.18em', textTransform:'uppercase', color:C.g600, marginBottom:'.65rem', display:'flex', alignItems:'center', justifyContent:'center', gap:10 }}>
              <span style={{ display:'inline-block', width:28, height:1.5, background:C.g500 }} />Our Journey
            </div>
            <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.8rem,3.5vw,2.75rem)', color:C.g900 }}>15+ Years of Care</h2>
          </div>
          <div style={{ position:'relative' }}>
            {/* vertical line */}
            <div style={{ position:'absolute', left:'50%', top:0, bottom:0, width:2, background:C.border, transform:'translateX(-50%)', zIndex:0 }} />
            {TIMELINE.map((t, i) => (
              <TimelineItem key={t.year} {...t} isLeft={i % 2 === 0} />
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div style={{ background:C.g50, padding:'0 2rem 5rem' }}>
        <div style={{ maxWidth:1600, margin:'0 auto' }}>
          <div style={{ background:`linear-gradient(130deg,${C.g900},${C.g700})`, borderRadius:24, padding:'3.5rem 4rem', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'2rem', flexWrap:'wrap' }}>
            <div>
              <h2 style={{ fontFamily:"'DM Serif Display',serif", fontSize:'clamp(1.75rem,3vw,2.5rem)', color:'white', marginBottom:'.75rem', lineHeight:1.15 }}>
                Ready to start your<br/>skin journey?
              </h2>
              <p style={{ color:'rgba(255,255,255,.6)', fontSize:'.9rem', maxWidth:420, lineHeight:1.75 }}>
                Join patients across India who trust Revive for expert, evidence-based dermatological care.
              </p>
            </div>
            <div style={{ display:'flex', gap:'1rem', flexWrap:'wrap', flexShrink:0 }}>
              <button onClick={() => navigate('/book-appointment')} style={{ background:C.g400, color:C.g900, padding:'.9rem 2.25rem', borderRadius:10, fontSize:'.875rem', fontWeight:600, border:'none', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", letterSpacing:'.04em', boxShadow:'0 4px 20px rgba(90,174,115,.35)' }}>
                Book Consultation — ₹500
              </button>
              <Link to="/shop" style={{ background:'rgba(255,255,255,.1)', color:'white', padding:'.9rem 2.25rem', borderRadius:10, fontSize:'.875rem', fontWeight:500, border:'1px solid rgba(255,255,255,.2)', textDecoration:'none', display:'inline-flex', alignItems:'center' }}>
                Shop Products
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

function ValueCard({ Icon, title, desc }) {
  const [hov, setHov] = React.useState(false)
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? `linear-gradient(135deg,${C.g700},${C.g500})` : C.g50, border:`1px solid ${hov ? C.g500 : C.border}`, borderRadius:18, padding:'1.75rem', transition:'all .35s', cursor:'default', boxShadow: hov ? '0 12px 36px rgba(45,107,66,.18)' : 'none', transform: hov ? 'translateY(-4px)' : 'none' }}>
      <div style={{ width:50, height:50, borderRadius:12, background: hov ? 'rgba(255,255,255,.15)' : C.g100, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'1.25rem', transition:'background .35s' }}>{Icon && <Icon size={22} strokeWidth={1.75} color={hov?'#fff':C.g700} />}</div>
      <h4 style={{ fontSize:'1rem', fontWeight:600, color: hov ? 'white' : C.g800, marginBottom:'.5rem', transition:'color .35s' }}>{title}</h4>
      <p style={{ fontSize:'.83rem', color: hov ? 'rgba(255,255,255,.7)' : C.muted, lineHeight:1.7, transition:'color .35s' }}>{desc}</p>
    </div>
  )
}

function DoctorCard({ initial, name, qual, extra, tag, bg }) {
  const [hov, setHov] = React.useState(false)
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:20, overflow:'hidden', transition:'all .3s', boxShadow: hov ? '0 16px 56px rgba(13,34,24,.14)' : 'none', transform: hov ? 'translateY(-5px)' : 'none' }}>
      <div style={{ height:190, background:bg, display:'flex', alignItems:'flex-end', justifyContent:'flex-start', padding:'1.5rem', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:'8rem', color:'white', opacity:.07, lineHeight:1 }}>{initial}</div>
        <div style={{ width:64, height:64, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'2px solid rgba(255,255,255,.3)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Serif Display',serif", fontSize:'1.6rem', color:'white', position:'relative', zIndex:1 }}>{initial}</div>
      </div>
      <div style={{ padding:'1.5rem' }}>
        <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.2rem', color:C.g900, marginBottom:'.3rem' }}>{name}</div>
        <div style={{ fontSize:'.78rem', color:C.muted, lineHeight:1.65, marginBottom:'.4rem' }}>{qual}</div>
        <div style={{ fontSize:'.78rem', color:C.muted, lineHeight:1.65, marginBottom:'1rem' }}>{extra}</div>
        <span style={{ display:'inline-flex', alignItems:'center', gap:6, background:C.g100, color:C.g700, padding:'.3rem .85rem', borderRadius:999, fontSize:'.72rem', fontWeight:600 }}>✦ {tag}</span>
      </div>
    </div>
  )
}

function TimelineItem({ year, title, desc, isLeft }) {
  return (
    <div style={{ display:'flex', justifyContent: isLeft ? 'flex-end' : 'flex-start', paddingRight: isLeft ? 'calc(50% + 2rem)' : 0, paddingLeft: isLeft ? 0 : 'calc(50% + 2rem)', marginBottom:'2.5rem', position:'relative', zIndex:1 }}>
      {/* dot */}
      <div style={{ position:'absolute', left:'50%', top:'1.25rem', transform:'translate(-50%,-50%)', width:14, height:14, borderRadius:'50%', background:C.g700, border:`3px solid white`, boxShadow:`0 0 0 2px ${C.g400}`, zIndex:2 }} />
      <div style={{ background:'white', border:`1px solid ${C.border}`, borderRadius:16, padding:'1.5rem', maxWidth:320, boxShadow:'0 2px 8px rgba(13,34,24,.06)', transition:'all .3s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor=C.g400; e.currentTarget.style.boxShadow='0 4px 24px rgba(13,34,24,.1)' }}
        onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.boxShadow='0 2px 8px rgba(13,34,24,.06)' }}>
        <div style={{ display:'inline-block', background:C.g100, color:C.g700, padding:'.2rem .75rem', borderRadius:999, fontSize:'.75rem', fontWeight:700, marginBottom:'.75rem', letterSpacing:'.04em' }}>{year}</div>
        <div style={{ fontFamily:"'DM Serif Display',serif", fontSize:'1.05rem', color:C.g900, marginBottom:'.4rem' }}>{title}</div>
        <div style={{ fontSize:'.82rem', color:C.muted, lineHeight:1.7 }}>{desc}</div>
      </div>
    </div>
  )
}
